import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import css from 'classnames'
import { XYCoord, useDrag, useDrop } from 'react-dnd'
import { Link } from 'react-router-dom'
import { NestedCategories } from '~/shared/api'
import {
  findById,
  insertElementWithShift,
  recursiveRemove,
} from '../../lib/helpers'
import { SubItem } from './sub-item-1'
import { DropZone } from './drop-zone'
import { CategoryStatusIndicators } from './category-status-indicators'
import { SVGDragIndicator, ArrowToDown, SVGPencil } from '~/shared/ui'

export interface Props {
  data: NestedCategories
  updateList: (draggingId: string, targetItem: NestedCategories) => void
  categoriesList: NestedCategories[]
  index: number
  setList: Dispatch<SetStateAction<NestedCategories[]>>
  updated: () => void
}

export const CategoryItem = ({
  data,
  index,
  categoriesList,
  setList,
  updateList,
  updated,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const dragPreviewRef = useRef<HTMLDivElement | null>(null)

  const [expanded, setExpanded] = useState(data.categories?.length > 0)

  useEffect(() => {
    if (data.categories?.length > 0) {
      setExpanded(true)
    }
  }, [data.categories])

  const [, drop] = useDrop({
    accept: 'category',

    hover: (item: NestedCategories & { index: number }, monitor) => {
      if (!dragPreviewRef.current) return

      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) return

      const isOverShallow = monitor.isOver({ shallow: true })

      if (!isOverShallow) return

      const hoveredRect = dragPreviewRef.current.getBoundingClientRect()
      const hoverMiddleY = (hoveredRect.bottom - hoveredRect.top) / 2
      const mousePosition = monitor.getClientOffset() as XYCoord
      const hoverClientY = mousePosition.y - hoveredRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      const newItems = [...categoriesList]
      const [draggedItem] = newItems.splice(dragIndex, 1)
      newItems.splice(hoverIndex, 0, draggedItem)

      setList(newItems)
      updated()

      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'category',
    item: { ...data, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(ref)
  drop(preview(dragPreviewRef))

  const [{ canDrop, isOver }, dropParent] = useDrop({
    accept: ['sub-category', 'sub-category-2', 'sub-category-3'],

    drop: (item: NestedCategories) => {
      if (ref.current) {
        const idx = Number(ref.current.getAttribute('data-index'))

        const filtered = recursiveRemove(categoriesList, item.id)
        const newData = insertElementWithShift(filtered, idx, item)

        setList(newData)

        updated()
      }
    },

    canDrop: (item) => {
      const el = findById(data.categories || [], item.id)

      return !el
    },

    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
    }),
  })

  const [collect, dropDrag] = useDrop({
    accept: ['sub-category', 'sub-category-2', 'sub-category-3'],

    drop: (item: NestedCategories) => {
      const filtered = recursiveRemove(categoriesList, item.id)
      const newData = insertElementWithShift(filtered, 1, item)

      setList(newData)

      updated()
    },

    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  })

  return (
    <div data-id={data.id}>
      <div ref={dragPreviewRef}>
        <div
          className={css(
            'category relative w-full overflow-hidden rounded-lg border border-[#E0E0E0] bg-white transition-all active:overflow-hidden',
            {
              'h-[56px]': !expanded,
              'h-[auto]': expanded,
              'opacity-50': isDragging || (isOver && canDrop),
            },
          )}
          data-index={index}
        >
          <div
            className="relative flex h-[55px] min-w-0 items-center"
            data-parent={`parent-${data.id}`}
            ref={dropParent}
          >
            <div
              ref={ref}
              data-index={index}
              className="flex cursor-grab item-center shrink-0 justify-center p-3 active:cursor-grabbing"
            >
              <SVGDragIndicator />
            </div>

            <div className="flex min-w-0 w-full items-center pr-3">
              <div className="flex min-w-0 flex-1 items-center">
                <h2
                  className={css('min-w-0 flex-1 truncate text-[18px]', {
                    'opacity-50': !data.showInMenu,
                  })}
                >
                  {data.name}
                </h2>
              </div>

              <div className="ml-3 flex shrink-0 items-center gap-3">
                <CategoryStatusIndicators
                  enabled={data.enabled}
                  showInMenu={data.showInMenu}
                />

                <Link
                  to={`/categories/${data.id}/edit`}
                  className="group flex shrink-0 items-center justify-center cursor-pointer"
                >
                  <div
                    className={css('text-gray-400 group-hover:text-gray-600', {
                      'opacity-50': !data.showInMenu,
                    })}
                  >
                    <SVGPencil />
                  </div>
                </Link>
              </div>

              <span
                onClick={() => setExpanded(!expanded)}
                className="ml-auto flex shrink-0 cursor-pointer items-center justify-center p-1.5 text-end"
              >
                <div
                  className={css('cursor-pointer transition-all duration-75', {
                    'rotate-180': expanded,
                  })}
                >
                  <ArrowToDown />
                </div>
              </span>
            </div>
          </div>

          <div className={css('p-3')}>
            <hr className="mt-2" />

            <div className="my-3">
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                {data?.categories?.map((item, idx) => (
                  <SubItem
                    setList={setList}
                    parentItem={data}
                    data={item}
                    key={item.id}
                    subList={data.categories || []}
                    list={categoriesList}
                    index={idx}
                    updateList={updateList}
                    updated={updated}
                  />
                ))}
              </div>
            </div>

            <div className="flex h-[100px] min-w-[350px] w-full flex-1">
              <DropZone updateList={updateList} parentElement={data} />
            </div>
          </div>
        </div>
      </div>

      {categoriesList.length === 1 ? (
        <div
          ref={dropDrag}
          className={css('mt-2 h-[56px] w-full rounded', {
            'bg-gray-200 opacity-70': collect.isOver,
          })}
        />
      ) : null}
    </div>
  )
}
